// Business outcome classifier for Prisma Ortho (and any tenant that opts
// in via botCatalog.json → bots[].business_outcomes).
//
// Problem this solves:
//   `lastAction` on a session is just the LAST tool invoked, not necessarily
//   the one that succeeded or the business event we care about. Example:
//   AppointmentActions calls ScheduleAppointment (reschedule succeeds), then
//   says "anything else?" — if caller says no and the session ends via
//   Agent handover, `lastAction = Agent handover` and `interactionOutcome =
//   ESCALATED` — but a reschedule DID happen.
//
//   For a trustworthy "how many reschedules yesterday?" number, we need
//   per-invocation data from the per-session messages, not just the summary
//   session row.
//
// Approach:
//   TWO modes a report can run in — the UI exposes a toggle so the operator
//   can trade speed for accuracy:
//
//   1. FAST (default) — session-row classifier only, no extra MCP calls:
//        count sessions where `agentName` matches a business-outcome's
//        source bot AND `lastAction` is in the outcome's tool-name set.
//        No restriction on `interactionOutcome` — covers HANDLED, ESCALATED,
//        ABANDONED-after-tool-fire, etc. This is a LOOSE LOWER-BOUND: it
//        misses sessions where the success tool fired mid-session but a
//        different tool was the final call.
//
//   2. DEEP (opt-in) — same base filter PLUS per-session message fetch:
//        for each candidate session, pull messages, scan every bot message's
//        metadata.tools[] for the outcome's tool-name set. Count sessions
//        with AT LEAST ONE invocation. This is the GROUND TRUTH count of
//        sessions where the success tool was invoked, regardless of what
//        happened after. Rate-limited at ~1 req / 2.2s (Cisco API cap), so
//        a ~200-session window takes ~7-8 minutes.
//
// Config shape (per-bot in botCatalog.json):
//   {
//     "bots": {
//       "PROD-Prisma_AppointmentActions": {
//         ...,
//         "business_outcomes": {
//           "Rescheduled Appt": {
//             "tool_names": ["ScheduleAppointment"],
//             "description": "Caller rescheduled an existing appointment."
//           },
//           "Confirmed Appt": { "tool_names": ["ConfirmAppointment"] },
//           "Cancelled Appt": { "tool_names": ["CancelAppointment"] }
//         }
//       }
//     }
//   }
//
// Why this is a lib not a hard-coded report: other tenants (future) will
// want their own outcome labels. The classifier is tenant-agnostic — it
// reads outcomes from botCatalog.json and maps whatever's there.

const PROD_BOT_PREFIX = 'PROD-';

/**
 * Build a flat list of outcome definitions from a bot catalog.
 * Shape: [{ outcome, botName, toolNames: Set<string>, description }]
 */
export function listOutcomes(catalog) {
  const out = [];
  for (const [botName, bot] of Object.entries(catalog?.bots ?? {})) {
    const defs = bot?.business_outcomes ?? {};
    for (const [outcome, def] of Object.entries(defs)) {
      out.push({
        outcome,
        botName,
        toolNames: new Set(def.tool_names ?? []),
        description: def.description ?? '',
      });
    }
  }
  return out;
}

/**
 * FAST classifier — session-row only, uses lastAction.
 *
 * @param {Array} sessions - raw contactAIAgentSession rows (one per session)
 * @param {object} catalog - botCatalog.json
 * @returns {object} {
 *   outcomes: { "<label>": N, ... },           // per-outcome session count
 *   fallout: { "<botName>": { total, handled, escalated, abandoned, errored }, ... },
 *   alsoRanHandled: [{ botName, lastAction, count }, ...],  // HANDLED w/ no outcome match
 *   nonProdLeakage: [{ botName, count, sessions: [...], prodCounterpart }, ...],
 * }
 */
export function classifyFast(sessions, catalog, opts = {}) {
  const defs = listOutcomes(catalog);
  const outcomes = Object.fromEntries(defs.map((d) => [d.outcome, 0]));
  const fallout = new Map();
  const alsoRanMap = new Map();
  const nonProdByBot = new Map();

  // Build a set of "declared outcome tool names" per bot, so we can detect
  // HANDLED sessions whose lastAction doesn't match any outcome tool.
  const outcomeToolsByBot = new Map();
  for (const d of defs) {
    if (!outcomeToolsByBot.has(d.botName)) outcomeToolsByBot.set(d.botName, new Set());
    for (const t of d.toolNames) outcomeToolsByBot.get(d.botName).add(t);
  }

  // Build a reverse index of (botName|toolName) → outcome label for fast match.
  const toolToOutcome = new Map();
  for (const d of defs) {
    for (const t of d.toolNames) {
      toolToOutcome.set(`${d.botName}|${t}`, d.outcome);
    }
  }

  // Build a set of prod bot names + a stem→prod map so we can detect
  // non-prod leakage (session on bot "FooBar" when "PROD-FooBar" exists).
  const prodBotNames = new Set(Object.keys(catalog?.bots ?? {}));
  const prodStems = new Map();
  for (const bn of prodBotNames) {
    if (bn.startsWith(PROD_BOT_PREFIX)) prodStems.set(bn.slice(PROD_BOT_PREFIX.length).toLowerCase(), bn);
  }

  // agentId → canonical bot name map (opt-in). The Cisco search API
  // sometimes returns rows with an empty agentName but a populated agentId
  // (observed 2026-10-26: ~32% of OrthoMain sessions landed with empty
  // agentName). Resolving by agentId when agentName is blank fixes the
  // undercount — Business Outcomes is otherwise missing ~1/3 of real volume.
  // Pass opts.agentIdToName when calling (BusinessOutcomes.jsx hydrates
  // this from `list-ai-agents`).
  const agentIdToName = opts.agentIdToName ?? {};
  const resolveBotName = (s) => {
    const name = s.agentName ?? '';
    if (name) return name;
    const id = s.agentId ?? '';
    return (id && agentIdToName[id]) || '(unknown)';
  };

  for (const s of sessions) {
    const agent = resolveBotName(s);
    const outcome = s.interactionOutcome ?? 'UNKNOWN';
    const last = s.lastAction ?? '';

    // Business outcome match (lastAction only — fast mode).
    const matchKey = `${agent}|${last}`;
    const label = toolToOutcome.get(matchKey);
    if (label) outcomes[label] = (outcomes[label] ?? 0) + 1;

    // Fallout per bot (only for bots we have outcomes on — i.e. target bots).
    if (outcomeToolsByBot.has(agent)) {
      if (!fallout.has(agent)) fallout.set(agent, { total: 0, handled: 0, escalated: 0, abandoned: 0, errored: 0, unknown: 0 });
      const f = fallout.get(agent);
      f.total += 1;
      if (outcome === 'HANDLED') f.handled += 1;
      else if (outcome === 'ESCALATED') f.escalated += 1;
      else if (outcome === 'ABANDONED') f.abandoned += 1;
      else if (outcome === 'ERRORED') f.errored += 1;
      else f.unknown += 1;

      // Also-ran HANDLED: outcome=HANDLED but lastAction not in the bot's
      // business-outcome tool set → the session completed cleanly but
      // didn't produce a tracked business event. Worth surfacing.
      if (outcome === 'HANDLED' && !outcomeToolsByBot.get(agent).has(last)) {
        const k = `${agent}|${last || '(none)'}`;
        alsoRanMap.set(k, (alsoRanMap.get(k) ?? 0) + 1);
      }
    }

    // Non-prod leakage detection.
    if (!agent.startsWith(PROD_BOT_PREFIX)) {
      const stem = agent.toLowerCase();
      const prodMatch = prodStems.get(stem);
      if (prodMatch) {
        if (!nonProdByBot.has(agent)) nonProdByBot.set(agent, { botName: agent, prodCounterpart: prodMatch, count: 0, sessions: [] });
        const row = nonProdByBot.get(agent);
        row.count += 1;
        if (row.sessions.length < 20) row.sessions.push(s.sessionId);
      }
    }
  }

  const alsoRanHandled = [...alsoRanMap.entries()]
    .map(([k, count]) => { const [botName, lastAction] = k.split('|'); return { botName, lastAction, count }; })
    .sort((a, b) => b.count - a.count);

  return {
    outcomes,
    fallout: Object.fromEntries(fallout.entries()),
    alsoRanHandled,
    nonProdLeakage: [...nonProdByBot.values()].sort((a, b) => b.count - a.count),
    mode: 'fast',
  };
}

/**
 * DEEP classifier — same as fast PLUS per-session message fetch.
 * Returns the fast result enriched with `outcomesVerified` — the ground
 * truth per-outcome count based on actual tool invocations in messages.
 *
 * @param {Array} sessions - same session rows
 * @param {object} catalog - botCatalog.json
 * @param {object} opts - {
 *   fetchMessages: async (agentId, sessionId) => { messages: [...] },
 *   agentIdByName: { [botName]: agentId },
 *   onProgress: optional (done, total) => void,
 *   signal: optional AbortSignal
 * }
 */
export async function classifyDeep(sessions, catalog, opts) {
  // Pass the agentIdToName map through to the Fast pass so the fallout
  // table + totals also benefit from the empty-agentName fix.
  const agentIdToName = opts.agentIdToName ?? {};
  const fast = classifyFast(sessions, catalog, { agentIdToName });
  const defs = listOutcomes(catalog);
  const targetBots = new Set(defs.map((d) => d.botName));
  const toolToOutcome = new Map();
  for (const d of defs) for (const t of d.toolNames) toolToOutcome.set(`${d.botName}|${t}`, d.outcome);

  const outcomesVerified = Object.fromEntries(defs.map((d) => [d.outcome, 0]));
  const verifiedSessionsByOutcome = Object.fromEntries(defs.map((d) => [d.outcome, []]));

  // Resolve each session's canonical bot name (prefers agentName, falls back
  // to agentId→name lookup) BEFORE filtering target sessions. Otherwise the
  // empty-agentName sessions get silently dropped from the Deep pass.
  const resolveBotName = (s) => {
    const name = s.agentName ?? '';
    if (name) return name;
    const id = s.agentId ?? '';
    return (id && agentIdToName[id]) || '';
  };

  const targetSessions = sessions.filter((s) => targetBots.has(resolveBotName(s)));
  let done = 0;

  for (const s of targetSessions) {
    if (opts.signal?.aborted) break;
    const botName = resolveBotName(s);
    const agentId = opts.agentIdByName?.[botName];
    if (!agentId) { done += 1; opts.onProgress?.(done, targetSessions.length); continue; }
    let msgs = null;
    try {
      const r = await opts.fetchMessages(agentId, s.sessionId);
      msgs = r?.messages ?? [];
    } catch { msgs = []; }

    // Build a set of outcomes this session hit (de-dup within the session).
    // Tool metadata shape (verified against prod 2026-10-23):
    //   metadata.tools[].tool_name   — the Agent Studio tool name (e.g. "ScheduleAppointment")
    //   metadata.tools[].state       — "SUCCESS" when the fulfillment flow returned OK
    // We ONLY count invocations with state === "SUCCESS" so a failed Epic
    // write doesn't register as a business event. (A caller who abandoned
    // DURING a successful tool call still counts — the business event
    // happened in Epic regardless of session outcome.)
    const hitOutcomes = new Set();
    for (const m of msgs) {
      const tools = m?.metadata?.tools ?? [];
      for (const t of tools) {
        const toolName = t.tool_name ?? t.name ?? '(unknown)';
        if (t.state && t.state !== 'SUCCESS') continue;
        const key = `${botName}|${toolName}`;
        const label = toolToOutcome.get(key);
        if (label) hitOutcomes.add(label);
      }
    }
    for (const label of hitOutcomes) {
      outcomesVerified[label] += 1;
      verifiedSessionsByOutcome[label].push(s.sessionId);
    }
    done += 1;
    opts.onProgress?.(done, targetSessions.length);
  }

  return {
    ...fast,
    outcomesVerified,
    verifiedSessionsByOutcome,
    mode: 'deep',
  };
}

/**
 * Day-by-day trend for a single outcome, using FAST classifier (per-row only).
 * Buckets by day in the window's local TZ.
 */
export function trendByDay(sessions, catalog, outcomeLabel, tzOffsetMinutes = -new Date().getTimezoneOffset(), opts = {}) {
  const defs = listOutcomes(catalog);
  const def = defs.find((d) => d.outcome === outcomeLabel);
  if (!def) return [];
  const toolSet = def.toolNames;
  const agentIdToName = opts.agentIdToName ?? {};
  const resolveBotName = (s) => s.agentName || (s.agentId && agentIdToName[s.agentId]) || '';
  const byDay = new Map();
  for (const s of sessions) {
    if (resolveBotName(s) !== def.botName) continue;
    if (!toolSet.has(s.lastAction)) continue;
    const ts = Number(s.sessionStartTime ?? s.sessionEndTime ?? 0);
    if (!ts) continue;
    const d = new Date(ts + tzOffsetMinutes * 60_000);
    const day = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
    byDay.set(day, (byDay.get(day) ?? 0) + 1);
  }
  return [...byDay.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([day, count]) => ({ day, count }));
}
